import React, { FC, useEffect, useRef, useState } from "react";
import { Form, InputNumber as LibraryInputNumber, InputNumberProps, Select } from "antd";
import { GET_CURRENCIES } from "~/graphql/query/currencies-query";
import { useQuery } from "@apollo/client";
import { CurrenciesQuery } from "~/gql/graphql";

type Currency = { 
  code: string;
  symbol: string;
}



type Props = {
  currencies: { code: string; symbol: string }[];
  currency: { code: string; symbol: string };
  formKey: string;
  // ... other props
};

const selectStyles = {
  position: 'absolute' as const,
  left: '4px',
  top: '50%',
  transform: 'translateY(-50%)',
  width: 'auto',
  zIndex: 1,
};

const selectDropdownStyles = {
  position: 'absolute' as const,
  width: '100%',
  borderTopLeftRadius: 0,
  borderTopRightRadius: 0,
  borderTop: 'none',
  marginTop: -1,
};


export const CurrencyPriceInput: FC<Props> = ({
  currencies,
  currency,
  formKey,
  ...others
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<any>(null);
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const formInstance = Form.useFormInstance();
  
  const currencyFieldName = 'sharedCurrencyCode';
  const amountFieldName = `${formKey}`;
  
  const currentCurrencyCode = Form.useWatch(currencyFieldName, formInstance);
  const selectedCurrency = currencies.find(c => c.code === currentCurrencyCode) || currency;

  React.useEffect(() => {
    formInstance.setFieldValue(currencyFieldName, currency.code);
  }, [currency]);


  return (
    <div style={{ position: "relative", width: "100%" }} ref={containerRef}>
      <Form.Item name={amountFieldName} noStyle>
        <LibraryInputNumber
          key={`${formKey}-${selectedCurrency.code}`}
          {...others}
          style={{
            width: "100%",
            paddingLeft: "65px",
            ...(dropdownOpen
              ? {
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                }
              : {}),
          }}
          ref={inputRef}
          controls={false}
          disabled={dropdownOpen}
          onFocus={() => {
            if (inputRef.current) {
              inputRef.current.select();
            }
          }}
          formatter={(value: string | number | undefined) => {
            if (value === undefined || value === "" || isNaN(Number(value))) {
              return "";
            }
            const numericValue = typeof value === "number" ? value : parseFloat(value);
            return `${selectedCurrency.symbol} ${numericValue.toLocaleString()}`;
          }}
          parser={(v: string | undefined) => {
            if (!v) return "";
            const parsed = v
              .replace(new RegExp(`[${selectedCurrency.symbol}]`, "g"), "")
              .replace(/,/g, "")
              .trim();
            return parsed;
          }}
        />
      </Form.Item>
      <Form.Item name={currencyFieldName} noStyle>
        <Select
          key={currency.code + formKey + "select"}
          open={dropdownOpen}
          onDropdownVisibleChange={setDropdownOpen}
          onChange={(value) => {
            const selectedCurrency = currencies.find((currency) => currency.code === value);
            if (selectedCurrency) {
              formInstance.setFieldValue(currencyFieldName, selectedCurrency.code);
            }
            setDropdownOpen(false);
          }}
          dropdownMatchSelectWidth={false}
          getPopupContainer={() => containerRef.current!}
          options={currencies.map((currency) => ({
            label: `${currency.code}`,
            value: currency.code,
          }))}
          style={selectStyles}
          dropdownStyle={selectDropdownStyles}
          bordered={false}
          listItemHeight={10}
          listHeight={200}
          className="currency-select-no-padding"
        />
      </Form.Item>
    </div>
  );
}; 